import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectSummary } from '../../../../types/ProjectSummary';
import { Button } from '../../../FormComponents/Buttons/Button';
import BaseProjectCard from '../BaseProjectCard/BaseProjectCard';
import {
  getFriendlyDuration,
  getFriendlyDate,
  getFriendlyElapsedDuration,
  getTimezoneName,
} from '../../../../helpers/dateFormatHelper/dateFormatHelper';
import history from '../../../../tools/history';

import './ProjectCard.scss';
import Row from '../../../Layout/Row/Row';
import { CardTypeEnum } from '../../../../types/enums/CardTypeEnum';

type ProjectCardProps = {
  cardType: CardTypeEnum;
  project: ProjectSummary | null;
  onShowProjectSummary: (projectId: string) => void;
};

const ProjectCard = ({ cardType, project, onShowProjectSummary }: ProjectCardProps) => {
  const { t } = useTranslation();

  const friendlyStartDate = getFriendlyDate(project?.scheduledDateTime);
  const friendlyStreamedDate = !project?.broadcastStarted
    ? t('project.not-streamed')
    : getFriendlyDate(project?.broadcastStarted);
  
  const projectStatus = project?.broadcastStarted ? t('project.status-live'): t('project.status-scheduled');
  const projectStatusColumnClass = project?.broadcastStarted ? 'project-card-row-content-live': 'project-card-row-content';
  const friendlyDuration = getFriendlyDuration(project?.scheduledDuration ?? 0);
  const friendlyElapsedTime = !project?.duration
    ? (!project?.broadcastStarted ? t('project.not-elapsed') : '< 1m' )
    : getFriendlyElapsedDuration(project?.duration.toString() || '');

  const viewProjectSummaryCard = (projectId: string) => {
    onShowProjectSummary(projectId);
  };

  if (!project) {
    return null;
  }

  const openProducerPage = (projectId: string | undefined) => {
    if (projectId) {
      history.push('/producer/' + projectId);
    }
  };

  return (
    <BaseProjectCard>
      <div className="project-card-contents">
        <h3 className="project-card-heading">{project?.name}</h3>
        <div className="project-card-details">
          <Row
            left={t('project.scheduled-for')}
            right={friendlyStartDate}
            columnClass="project-card-column"
            leftClass="project-card-row-title"
            rightClass="project-card-row-content"
          />
          {cardType === CardTypeEnum.Active ? (
            <>
              <Row
                left={t('project.time-zone')}
                right={getTimezoneName(
                  project?.scheduledDateTime,
                  project?.scheduledTime,
                  project?.scheduledTimeZone,
                )}
                columnClass="project-card-column"
                leftClass="project-card-row-title"
                rightClass="project-card-row-content"
              />
              <Row
                left={t('project.status-header')}
                right={projectStatus}
                columnClass="project-card-column"
                leftClass="project-card-row-title"
                rightClass={projectStatusColumnClass}
              />
              <Row
                left={t('project.duration')}
                right={friendlyDuration}
                columnClass="project-card-column"
                leftClass="project-card-row-title"
                rightClass="project-card-row-content"
              />
            </>
          ) : (
            <>
              <Row
                left={t('project.streamed')}
                right={friendlyStreamedDate}
                columnClass="project-card-column"
                leftClass="project-card-row-title"
                rightClass="project-card-row-content"
              />
              <Row
                left={t('project.elapsed-duration')}
                right={friendlyElapsedTime}
                columnClass="project-card-column"
                leftClass="project-card-row-title"
                rightClass="project-card-row-content"
              />
            </>
          )}
        </div>
        {cardType === CardTypeEnum.Active ? (
          <Button
            translationKey="button.open"
            variant="secondary"
            color="blue"
            additionalClassName="btn-no-margin"
            onClick={() => openProducerPage(project?.id)}
            type="button"
          />
        ) : (
          <Button
            translationKey="button.view-summary"
            variant="secondary"
            color="blue"
            additionalClassName="btn-no-margin btn-is-full"
            onClick={() => viewProjectSummaryCard(project.id)}
            type="button"
          />
        )}
      </div>
    </BaseProjectCard>
  );
};

export default ProjectCard;
